import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Grid, Divider, FormControlLabel, Checkbox } from '@material-ui/core';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import useUserActionLog, {
  Enum_Action_Button_Enum,
  CapaActivityType
} from '@hooks/useUserActionLog';
import { useStyles } from './styles';
import FullTextDialog from '@components/Dialog/FullText/FullTextDialog';
import AccordionPresenter from '../AccordionPresenter';

export interface TermsAndServiceState {
  serviceAgree: boolean;
  privacyAgree: boolean;
  paymentAgree: boolean;
  promotionAgree: boolean;
}

type Terms =
  | 'serviceAgree'
  | 'privacyAgree'
  | 'promotionAgree'
  | 'paymentAgree';

interface TermsAndServiceAgreementProps {
  setAgreeState: (state: TermsAndServiceState) => void;
  agreeState: TermsAndServiceState;
  checked: boolean;
}

const TermsAndServiceAgreement: React.FC<TermsAndServiceAgreementProps> = ({
  setAgreeState,
  agreeState,
  checked
}: TermsAndServiceAgreementProps) => {
  const { onInsertUserActionLogOne } = useUserActionLog();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [currentTerm, setCurrentTerm] = useState<Terms>('serviceAgree');
  const { termsSection, checkBoxLabel, checkBox } = useStyles();
  const { register, trigger } = useFormContext();

  const initialAgreeState = {
    serviceAgree: false,
    privacyAgree: false,
    promotionAgree: false,
    paymentAgree: false
  };

  return (
    <Grid className={termsSection}>
      <FullTextDialog
        openState={isOpen}
        content={currentTerm}
        onClose={() => setIsOpen(false)}
      />
      <Divider />
      <AccordionPresenter
        summary={
          <FormControlLabel
            onClick={(e) => e.stopPropagation()}
            className={checkBoxLabel + ' master'}
            control={
              <Checkbox
                className={checkBox}
                checked={checked}
                onChange={() => {
                  onInsertUserActionLogOne(
                    Enum_Action_Button_Enum.Button_SignUp_Agree_ServiceAndTerms,
                    CapaActivityType.ButtonClick
                  );

                  if (checked) {
                    setAgreeState(initialAgreeState);
                    trigger([
                      'serviceAgree',
                      'privacyAgree',
                      'promotionAgree',
                      'paymentAgree'
                    ]);
                  } else {
                    setAgreeState({
                      serviceAgree: true,
                      privacyAgree: true,
                      promotionAgree: true,
                      paymentAgree: true
                    });
                    trigger([
                      'serviceAgree',
                      'privacyAgree',
                      'promotionAgree',
                      'paymentAgree'
                    ]);
                  }
                }}
              />
            }
            label={
              <CustomizedTypography variant="body2" fontWeight="700">
                약관에 <b className="agreeAll">전체 동의</b>합니다
              </CustomizedTypography>
            }
          />
        }
        detail={
          <Grid container direction="column">
            <Grid container alignItems="center" justifyContent="space-between">
              <FormControlLabel
                className={checkBoxLabel}
                control={
                  <Checkbox
                    className={checkBox}
                    checked={agreeState.serviceAgree}
                    value={agreeState.serviceAgree ? true : false}
                    inputProps={{ name: 'serviceAgree' }}
                    inputRef={register}
                    onChange={() =>
                      setAgreeState({
                        ...agreeState,
                        serviceAgree: !agreeState.serviceAgree
                      })
                    }
                  />
                }
                label={
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                  >
                    <Grid item>
                      <CustomizedTypography variant="body2">
                        <b>[필수]</b> 캐파서비스 이용약관 동의
                      </CustomizedTypography>
                    </Grid>

                    <Grid item></Grid>
                  </Grid>
                }
              />
              <CustomizedButton
                variant="text"
                color="primary"
                onClick={() => {
                  setCurrentTerm('serviceAgree');
                  setIsOpen(true);
                }}
              >
                <CustomizedTypography
                  className="allContentViewer"
                  fontWeight="500"
                  variant="body2"
                  color="textSecondary"
                >
                  전문보기
                </CustomizedTypography>
              </CustomizedButton>
            </Grid>
            <Grid container alignItems="center" justifyContent="space-between">
              <FormControlLabel
                className={checkBoxLabel}
                control={
                  <Checkbox
                    className={checkBox}
                    checked={agreeState.privacyAgree}
                    value={agreeState.privacyAgree ? true : false}
                    inputProps={{ name: 'privacyAgree' }}
                    inputRef={register}
                    onChange={() => {
                      onInsertUserActionLogOne(
                        Enum_Action_Button_Enum.Button_SignUp_Agree_ServiceAndTerms,
                        CapaActivityType.ButtonClick
                      );
                      setAgreeState({
                        ...agreeState,
                        privacyAgree: !agreeState.privacyAgree
                      });
                    }}
                  />
                }
                label={
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                  >
                    <Grid item>
                      <CustomizedTypography variant="body2">
                        <b>[필수]</b> 개인정보 수집 및 활용 동의
                      </CustomizedTypography>
                    </Grid>
                    <Grid item></Grid>
                  </Grid>
                }
              />
              <CustomizedButton
                variant="text"
                color="primary"
                onClick={() => {
                  setCurrentTerm('privacyAgree');
                  setIsOpen(true);
                }}
              >
                <CustomizedTypography
                  className="allContentViewer"
                  variant="body2"
                  fontWeight="500"
                  color="textSecondary"
                >
                  전문보기
                </CustomizedTypography>
              </CustomizedButton>
            </Grid>
            <Grid container alignItems="center" justifyContent="space-between">
              <FormControlLabel
                className={checkBoxLabel}
                control={
                  <Checkbox
                    className={checkBox}
                    checked={agreeState.paymentAgree}
                    value={agreeState.paymentAgree ? true : false}
                    inputProps={{ name: 'paymentAgree' }}
                    inputRef={register}
                    onChange={() =>
                      setAgreeState({
                        ...agreeState,
                        paymentAgree: !agreeState.paymentAgree
                      })
                    }
                  />
                }
                label={
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                  >
                    <Grid item>
                      <CustomizedTypography variant="body2">
                        <b>[필수]</b> 캐파결제 서비스 약관 동의
                      </CustomizedTypography>
                    </Grid>

                    <Grid item></Grid>
                  </Grid>
                }
              />
              <CustomizedButton
                variant="text"
                color="primary"
                onClick={() => {
                  setCurrentTerm('paymentAgree');
                  setIsOpen(true);
                }}
              >
                <CustomizedTypography
                  className="allContentViewer"
                  fontWeight="500"
                  variant="body2"
                  color="textSecondary"
                >
                  전문보기
                </CustomizedTypography>
              </CustomizedButton>
            </Grid>
            <Grid container alignItems="center" justifyContent="space-between">
              <FormControlLabel
                className={checkBoxLabel}
                control={
                  <Checkbox
                    className={checkBox}
                    checked={agreeState.promotionAgree}
                    value={agreeState.promotionAgree ? true : false}
                    inputProps={{ name: 'promotionAgree' }}
                    inputRef={register}
                    onChange={() => {
                      onInsertUserActionLogOne(
                        Enum_Action_Button_Enum.Button_SignUp_Agree_Marketing,
                        CapaActivityType.ButtonClick
                      );
                      setAgreeState({
                        ...agreeState,
                        promotionAgree: !agreeState.promotionAgree
                      });
                    }}
                  />
                }
                label={
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                  >
                    <Grid item>
                      <CustomizedTypography variant="body2">
                        [선택] 캐파 소식 및 혜택 수신 동의
                      </CustomizedTypography>
                    </Grid>
                    <Grid item></Grid>
                  </Grid>
                }
              />
              <CustomizedButton
                variant="text"
                color="primary"
                onClick={() => {
                  setCurrentTerm('promotionAgree');
                  setIsOpen(true);
                }}
              >
                <CustomizedTypography
                  className="allContentViewer"
                  variant="body2"
                  fontWeight="500"
                  color="textSecondary"
                >
                  전문보기
                </CustomizedTypography>
              </CustomizedButton>
            </Grid>
          </Grid>
        }
      />
    </Grid>
  );
};

export default TermsAndServiceAgreement;
